import React, { useState, useMemo } from 'react';
import { User } from '../../types';
import { useAuth } from '../../AuthContext';
import { KeyRound, Mail, ExternalLink, Info, AlertTriangle, ChevronDown } from 'lucide-react';

interface SecuritySectionProps {
  user: User;
  onAccountDelete: () => Promise<void>;
}

const SecuritySection: React.FC<SecuritySectionProps> = ({ user, onAccountDelete }) => {
  const { currentUser, resetPassword } = useAuth();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showDangerZone, setShowDangerZone] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetError, setResetError] = useState('');

  // Detect auth provider from Firebase user
  const authProvider = useMemo(() => {
    if (!currentUser) return 'unknown';
    const providers = currentUser.providerData?.map(p => p.providerId) || [];
    if (providers.includes('google.com')) return 'google';
    if (providers.includes('password')) return 'email';
    return 'unknown';
  }, [currentUser]);

  const handlePasswordReset = async () => {
    if (!user.email) return;
    setResetError('');
    try {
      await resetPassword(user.email);
      setResetSent(true);
      setTimeout(() => setResetSent(false), 5000);
    } catch (error) {
      console.error('Password reset error:', error);
      setResetError('Nepodařilo se odeslat email pro reset hesla. Zkuste to znovu.');
    }
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      await onAccountDelete();
    } catch (error) {
      console.error('Error deleting account:', error);
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirmation(false);
    }
  };

  const formatDate = (date: string | Date | null) => {
    if (!date) return 'Neznámé';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('cs-CZ', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-8">
      {/* Account Information */}
      <div>
        <h3 className="text-lg font-bold tracking-tight text-gray-900 dark:text-white mb-4">
          Informace o účtu
        </h3>
        <div className="bg-gray-50 dark:bg-gray-700/30 rounded-2xl p-5 border-2 border-gray-100 dark:border-gray-700 space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-sm font-bold text-gray-700 dark:text-gray-300">
              Způsob přihlášení:
            </span>
            <div className="flex items-center space-x-2">
              {authProvider === 'google' ? (
                <KeyRound className="w-5 h-5 text-primary" />
              ) : (
                <Mail className="w-5 h-5 text-primary" />
              )}
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {authProvider === 'google' ? 'Google Sign-In' : 'Email a heslo'}
              </span>
            </div>
          </div>

          <div className="flex justify-between items-center">
            <span className="text-sm font-bold text-gray-700 dark:text-gray-300">
              Email:
            </span>
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {user.email}
            </span>
          </div>

          <div className="flex justify-between items-center">
            <span className="text-sm font-bold text-gray-700 dark:text-gray-300">
              Účet vytvořen:
            </span>
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {formatDate(null)}
            </span>
          </div>

          <div className="flex justify-between items-center">
            <span className="text-sm font-bold text-gray-700 dark:text-gray-300">
              Poslední přihlášení:
            </span>
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {formatDate(null)}
            </span>
          </div>
        </div>
      </div>

      {/* Password Management */}
      <div>
        <h3 className="text-lg font-bold tracking-tight text-gray-900 dark:text-white mb-4">
          Správa hesla
        </h3>
        <div className="bg-primary/5 dark:bg-primary/10 border-2 border-primary/20 dark:border-primary/30 rounded-2xl p-5">
          <div className="flex items-start space-x-3">
            <Info className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
            <div>
              {authProvider === 'google' ? (
                <>
                  <h4 className="text-sm font-bold text-gray-900 dark:text-gray-100">
                    Heslo spravuje Google
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                    Protože používáte Google Sign-In, vaše heslo je spravováno přímo Googlem.
                    Pro změnu hesla navštivte nastavení vašeho Google účtu.
                  </p>
                  <a
                    href="https://myaccount.google.com/security"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center mt-3 text-sm font-bold text-primary hover:text-primary/80 transition-colors duration-200"
                  >
                    Otevřít nastavení Google účtu
                    <ExternalLink className="w-4 h-4 ml-1" />
                  </a>
                </>
              ) : (
                <>
                  <h4 className="text-sm font-bold text-gray-900 dark:text-gray-100">
                    Změnit heslo
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                    Odešleme vám email s odkazem pro reset hesla na adresu {user.email}.
                  </p>
                  {resetSent && (
                    <p className="text-sm font-medium text-green-600 dark:text-green-400 mt-2">
                      Email pro reset hesla byl odeslán. Zkontrolujte svou schránku.
                    </p>
                  )}
                  {resetError && (
                    <p className="text-sm font-medium text-red-600 dark:text-red-400 mt-2">
                      {resetError}
                    </p>
                  )}
                  <button
                    onClick={handlePasswordReset}
                    disabled={resetSent}
                    className={`mt-3 px-5 py-2.5 text-sm font-bold rounded-2xl border-b-4 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:focus:ring-offset-gray-800 ${
                      resetSent
                        ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 cursor-not-allowed border-b-gray-400'
                        : 'bg-primary hover:bg-primary/90 text-primary-foreground border-b-amber-600 active:border-b-0 active:translate-y-1'
                    }`}
                  >
                    {resetSent ? 'Email odeslán' : 'Odeslat reset hesla'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Danger Zone - Collapsed */}
      <div className="border-t-2 border-gray-200 dark:border-gray-700 pt-6">
        {!showDangerZone ? (
          <button
            onClick={() => setShowDangerZone(true)}
            className="flex items-center gap-2 text-sm font-medium text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 transition-colors duration-200"
          >
            <AlertTriangle className="w-4 h-4" />
            <span>Zobrazit nebezpečnou zónu</span>
            <ChevronDown className="w-4 h-4" />
          </button>
        ) : (
          <div>
            <h3 className="text-lg font-bold tracking-tight text-red-600 dark:text-red-400 mb-4">
              Nebezpečná zóna
            </h3>
            <div className="bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-800 rounded-2xl p-5">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="w-6 h-6 text-red-500 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-red-900 dark:text-red-100">
                    Trvalé smazání účtu
                  </h4>
                  <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                    Smazání účtu je nevratné. Všechna vaše data, včetně pokroku ve studiu,
                    odznaků a nastavení budou trvale odstraněna.
                  </p>
                  <button
                    onClick={() => setShowDeleteConfirmation(true)}
                    className="mt-3 px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-bold rounded-2xl border-b-4 border-b-red-800 active:border-b-0 active:translate-y-1 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                  >
                    Smazat účet
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setShowDeleteConfirmation(false)}
            />

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-2xl px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6 border-2 border-gray-200 dark:border-gray-700">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-2xl bg-red-100 dark:bg-red-900/20 sm:mx-0 sm:h-10 sm:w-10">
                  <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-bold text-gray-900 dark:text-white">
                    Smazat účet
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Opravdu chcete smazat svůj účet? Tato akce je nevratná a všechna vaše data budou trvale odstraněna.
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                      Pro potvrzení budete přesměrováni na Google pro reauthentizaci.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse gap-3">
                <button
                  type="button"
                  onClick={handleDeleteAccount}
                  disabled={isDeleting}
                  className={`w-full sm:w-auto px-5 py-2.5 rounded-2xl text-sm font-bold text-white transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    isDeleting
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-red-600 hover:bg-red-700 focus:ring-red-500 border-b-4 border-b-red-800 active:border-b-0 active:translate-y-1'
                  }`}
                >
                  {isDeleting ? 'Mazání...' : 'Smazat účet'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirmation(false)}
                  disabled={isDeleting}
                  className="mt-3 sm:mt-0 w-full sm:w-auto px-5 py-2.5 rounded-2xl border-2 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-bold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
                >
                  Zrušit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecuritySection;